import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import { Card } from "@mui/material";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import { ImageDeletionDialog } from "@/components/molecules/ImageDeletionDialog";
import { getPublicImageUrl } from "@/helpers/image-url-helper";
import useProductImages from "@/hooks/use-product-images";
import { IProductImage } from "@/interfaces/product-image.interface";
import { ISku } from "@/interfaces/sku.interface";
import { ProductFormMessages } from "@/messages/products/product-form.messages";

import CarouselImages from "./carouselImages";

type Props = {
  messages: ProductFormMessages;
};

function ProductImage({ messages }: Props) {
  const images = useProductImages({ messages });

  const mainImage = images.mainImage;
  const hasFile = mainImage && "file" in mainImage;
  const previewImage = mainImage ? (mainImage.preview ?? "") : "";
  const urlImage = mainImage 
    ? (mainImage.url ?? getPublicImageUrl(mainImage.phisicalAddress) ?? "") 
    : "";
  const imageSrc = hasFile ? previewImage : urlImage;

  return (
    <Card
      sx={{
        "&:hover .card-header": {
          transform: "translate3d(0, -80px, 0)",
          zIndex: 10001,
        },

        height: "100%",
      }}
    >
      <MDBox
        position="relative"
        borderRadius="lg"
        mt={-3}
        mx={2}
        className={images.mainImage ? "card-header" : "none"}
        sx={{ transition: "transform 300ms cubic-bezier(0.34, 1.61, 0.7, 1)" }}
        height="300px"
        bgColor="white"
      >
        <MDBox
          borderRadius="lg"
          shadow="sm"
          width="100%"
          height="100%"
          position="relative"
          zIndex={10}
          mb={2}
          overflow="hidden"
          bgColor="white"
        >
          {images.mainImage ? (
            <img
              src={imageSrc}
              alt="Product Image"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <Card
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <ImageNotSupportedIcon fontSize="large" />
              <MDTypography variant="subtitle1">{messages.noImageSelected}</MDTypography>
            </Card>
          )}
        </MDBox>
      </MDBox>

      <MDBox textAlign="center" pt={2} pb={3} px={3}>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          mt={-10}
          position="relative"
          zIndex={1}
        >
          <MDButton
            variant="outlined"
            color="error"
            size="small"
            onClick={(event) => images.promptRemoveImage(images.mainImage as IProductImage, event)}
          >
            {messages.removeButton}
          </MDButton>
        </MDBox>

        <CarouselImages
          images={images.uniqueImages}
          removeImage={images.promptRemoveImage}
          setMainImage={images.handleSetMainImage}
          getRootProps={images.getRootProps}
          getInputProps={images.getInputProps}
        />
      </MDBox>

      <ImageDeletionDialog
        isOpen={images.isVisible}
        onClose={images.handleClose}
        onConfirm={images.handleConfirmDelete}
        image={images.imageToDelete as IProductImage}
        affectedSkus={images.affectedSkus as ISku[]}
        messages={{
          title: messages.confirmDeleteTitle,
          confirmButton: messages.deleteButton,
          cancelButton: messages.cancelButton,
          warningTitle: "Atenção!",
          warningDescription: "Esta imagem está sendo usada por {count} SKU(s):",
          confirmDescription: messages.confirmDeleteDescription,
          mainImageLabel: "Imagem Principal",
          affectedSkusLabel: "SKUs afetados",
        }}
      />
    </Card>
  );
}

export default ProductImage;
